/**
 * Les 25 icônes de la maquette, en SVG inline (aucune dépendance).
 * Trait 1.8, extrémités et jointures arrondies, viewBox 24 — transcrit tel quel
 * depuis `maquette/_extracted/parts/01-primitives/icon.js`.
 */

const PATHS = {
  grid: ['M4 4h6v6H4z', 'M14 4h6v6h-6z', 'M4 14h6v6H4z', 'M14 14h6v6h-6z'],
  cycle: [
    'M4 12a8 8 0 0 1 13.6-5.7L20 8',
    'M20 4v4h-4',
    'M20 12a8 8 0 0 1-13.6 5.7L4 16',
    'M4 20v-4h4',
  ],
  users: [
    'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M3 20a6 6 0 0 1 12 0',
    'M16 6.5a3 3 0 0 1 0 5.8',
    'M21 20a6 6 0 0 0-4-5.7',
  ],
  wallet: [
    'M3 7a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
    'M16 12h3',
    'M3 8h14',
  ],
  alert: ['M12 3 2 20h20z', 'M12 10v5', 'M12 18h.01'],
  spark: [
    'M12 3l1.8 5.4L19 10l-5.2 1.6L12 17l-1.8-5.4L5 10l5.2-1.6z',
    'M18 15l.6 1.8L20 17.4l-1.4.4L18 19l-.6-1.2L16 17.4l1.4-.6z',
  ],
  gear: [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2-1.2l-.3-2.5H10.7l-.3 2.5a7 7 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 2 1.2l.3 2.5h2.6l.3-2.5a7 7 0 0 0 2-1.2l2.3 1 2-3.4-2-1.5c.1-.4.1-.8.1-1.2z',
  ],
  bell: ['M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6z', 'M10 20a2 2 0 0 0 4 0'],
  check: ['M4 12l5 5L20 6'],
  plus: ['M12 5v14', 'M5 12h14'],
  arrow: ['M5 12h14', 'M13 6l6 6-6 6'],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2'],
  shield: ['M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z', 'M9 12l2 2 4-4'],
  phone: [
    'M4 5a2 2 0 0 1 2-2h2l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5V18a2 2 0 0 1-2 2A16 16 0 0 1 4 5z',
  ],
  eye: ['M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'],
  money: [
    'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  ],
  cal: ['M4 5h16v15H4z', 'M4 9h16', 'M8 3v4', 'M16 3v4'],
  trend: ['M4 15l5-5 3 3 6-7', 'M14 6h4v4'],
  logout: ['M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4', 'M9 12h11', 'M13 8l4 4-4 4'],
  menu: ['M4 7h16', 'M4 12h16', 'M4 17h16'],
  x: ['M6 6l12 12', 'M18 6L6 18'],
  chat: ['M4 5h16v11H9l-5 4z'],
  lock: ['M6 10h12v10H6z', 'M9 10V7a3 3 0 0 1 6 0v3'],
  pot: ['M6 8h12l-1 12H7z', 'M4 8h16', 'M9 4h6l1 4H8z'],
  hand: [
    'M7 12V6a1.5 1.5 0 0 1 3 0v5',
    'M10 11V4.5a1.5 1.5 0 0 1 3 0V11',
    'M13 11V6a1.5 1.5 0 0 1 3 0v6a6 6 0 0 1-6 6 6 6 0 0 1-5-3l-2-4a1.5 1.5 0 0 1 2.6-1.5L7 13',
  ],
} as const;

export type IconName = keyof typeof PATHS;

export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export type IconProps = {
  name: IconName;
  /** Côté du carré, en px. 20 par défaut, comme la maquette. */
  size?: number;
  /** Couleur du trait. `currentColor` par défaut : l'icône suit la couleur du texte. */
  color?: string;
  className?: string;
  /** Libellé accessible. Sans lui, l'icône est décorative et masquée aux lecteurs d'écran. */
  title?: string;
};

export function Icon({ name, size = 20, color = 'currentColor', className, title }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name].map((d) => (
        <path
          key={d}
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
